// src/app/router.jsx
import { lazy, Suspense } from 'react';
import { createBrowserRouter, Navigate, Outlet, useParams } from 'react-router';
import AppShell from '@app/AppShell';

// Lazy-loaded pages
const SignInPage = lazy(() => import('@pages/auth/Login'));
const ActivatePage = lazy(() => import('@pages/auth/Activate'));
const ResetPasswordPage = lazy(() => import('@pages/auth/ResetPasswordPage'));
const HomePage = lazy(() => import('@pages/HomePage'));
const VendorsListPage = lazy(() => import('@pages/vendors/VendorsListPage'));
const DerbyStatusManagementPage = lazy(() => import('@pages/vendors/DerbyStatusManagementPage'));
const EmployeesListPage = lazy(() => import('@pages/employees/EmployeeListPage'));
const EmployeeImportPage = lazy(() => import('@pages/employees/EmployeeImportPage'));
const HotelSearchPage = lazy(() => import('@pages/bookings/HotelSearchPage'));
const HotelDetailPage = lazy(() => import('@pages/bookings/HotelDetailPage'));
const ChangeBookingPage = lazy(() => import('@pages/bookings/ChangeBookingPage'));
const BookingGuestDetailsPage = lazy(() => import('@pages/bookings/BookingGuestDetailsPage'));
const BookingConfirmationPage = lazy(() => import('@pages/bookings/BookingConfirmationPage'));
const BookingFinalConfirmationPage = lazy(() => import('@pages/bookings/BookingFinalConfirmationPage'));
const GroupConfirmationPage = lazy(() => import('@pages/bookings/GroupConfirmationPage'));
const BookingPaymentPage = lazy(() => import('@pages/bookings/BookingPaymentPage'));
const MyReservationsPage = lazy(() => import('@pages/bookings/MyReservationsPage'));
const CustomerFeesPage = lazy(() => import('@pages/fees/CustomerFeesPage'));
const CustomersPage = lazy(() => import('@pages/customers/CustomersPage'));
const CustomerDetailPage = lazy(() => import('@pages/customers/CustomerDetailPage'));
const ChainsPage = lazy(() => import('@pages/chains/ChainsPage'));
const ProfilePage = lazy(() => import('@pages/users/ProfilePage'));
const UserManagement = lazy(() => import('../features/users/components/UserManagement'));
const BillingPage = lazy(() => import('@pages/billing/BillingPage'));
const BillingBatchDetailPage = lazy(() => import('@pages/billing/BillingBatchDetailPage'));
const BillingBatchesPage = lazy(() => import('@pages/billing/BillingBatchesPage'));
const HotelChargesPage = lazy(() => import('@pages/billing/HotelChargesPage'));
const StayLedgerPage = lazy(() => import('@pages/billing/StayLedgerPage'));
const ReportsShell = lazy(() => import('@pages/reports/ReportsShell'));
const LodgingAnalysisReportPage = lazy(() => import('@pages/reports/LodgingAnalysisReportPage'));
const InvoiceReportPage = lazy(() => import('@pages/reports/InvoiceReportPage'));
const RecordPaymentPage = lazy(() => import('@pages/billing/RecordPaymentPage'));
const RecordMultipleBatchPaymentPage = lazy(() => import('@pages/billing/RecordMultipleBatchPaymentPage'));
const PaymentExceptionsPage = lazy(() => import('@pages/billing/PaymentExceptionsPage'));
const PaymentsPage = lazy(() => import('@pages/billing/PaymentsPage'));
const StaffReservationsPage = lazy(() => import('@pages/staff-reservations/StaffReservationsPage'));
const CreateManualReservationPage = lazy(() => import('@pages/staff-reservations/CreateManualReservationPage'));
const VccCardsListPage = lazy(() => import('@pages/vcc-cards/VccCardsListPage'));
const IntegrationsPage = lazy(() => import('@pages/integrations/IntegrationsPage'));
const ErrorLogsPage = lazy(() => import('@pages/admin/ErrorLogsPage'));
const ErrorLogDetailPage = lazy(() => import('@pages/admin/ErrorLogDetailPage'));
const SeederPage = lazy(() => import('@pages/admin/SeederPage'));
const PoliciesPage = lazy(() => import('@pages/policies/PoliciesPage'));
const TagsPage = lazy(() => import('@pages/admin/TagsPage'));
const TagDetailPage = lazy(() => import('@pages/admin/TagDetailPage'));
const PolicyReviewsPage = lazy(() => import('@pages/policy-reviews/PolicyReviewsPage'));
import AuthProvider, { useAuth } from '@app/providers/AuthProvider';
import { SelectedRatesProvider } from '@features/bookings/context/SelectedRatesContext';
import RouteErrorBoundary from '@shared/components/feedback/RouteErrorBoundary';

const PageLoader = () => <div style={{ padding: '2rem', textAlign: 'center' }}>Loading…</div>;

// Boundary OUTSIDE Suspense so it catches both a render throw and a failed
// dynamic import. Inside, a rejected lazy() would escape past it.
const withSuspense = (Component) => (
  <RouteErrorBoundary>
    <Suspense fallback={<PageLoader />}>
      <Component />
    </Suspense>
  </RouteErrorBoundary>
);

// --- wrappers ---
function WithAuthProvider() {
  // NotificationProvider used to live here; it now mounts at the root in
  // main.jsx so it's available to boot-time errors too (login page,
  // password-reset page, axios interceptor at the very first request).
  return (
    <AuthProvider>
      <Outlet />
    </AuthProvider>
  );
}

function RequirePermission({ ability }) {
  const { user } = useAuth();
  const has = !!user?.roles?.some((r) =>
    r?.permissions?.some((p) => p?.name === ability)
  );
  return has ? <Outlet /> : <Navigate to="/user-home" replace />;
}

// Role-based gate. Use when a route is restricted to specific role names
// (e.g. "only MSI Staff / Admin / Super Admin can create manual reservations")
// rather than a granular permission. Travel Managers fail this check.
/** An old Reservation Ledger link, sent to the stay's page (2026-09-29). */
function RedirectToStay() {
  const { reservationId } = useParams();
  return <Navigate to={`/billing/stays/${reservationId}`} replace />;
}

// The old modify entry sent a traveller back through the hotel page to pick a
// rate again. Changes happen on the Change booking page now (ADR-0035); a
// saved link or browser history entry still lands somewhere that works.
function RedirectToChangeBooking() {
  const { confirmationId } = useParams();
  return <Navigate to={`/reservations/${encodeURIComponent(confirmationId)}/change`} replace />;
}

function RequireRole({ roles }) {
  const { user } = useAuth();
  const has = !!user?.roles?.some((r) => roles.includes(r?.name));
  return has ? <Outlet /> : <Navigate to="/user-home" replace />;
}

function Protected() {
  const { user, checking } = useAuth();
  if (checking) return null;
  return user ? <Outlet /> : <Navigate to="/login" replace />;
}

/**
 * The mirror of Protected: routes that only make sense when signed OUT.
 *
 * A session belongs to the browser, so opening /login in a second tab while
 * already signed in used to render the sign-in form — the app asking who you
 * are when it already knows. Worse than pointless: submitting it runs
 * Auth::login() + session()->regenerate() over the live session, which is also
 * the path by which one tab silently swaps the identity every other tab is
 * using.
 *
 * Standard equipment elsewhere — Laravel's `guest` middleware, Rails'
 * redirect_if_authenticated. This codebase had Protected and never its
 * counterpart.
 *
 * Signing out is unaffected: signOut empties the session before navigating, so
 * /login renders normally.
 */
function GuestOnly() {
  const { user, checking } = useAuth();
  if (checking) return null;
  return user ? <Navigate to="/user-home" replace /> : <Outlet />;
}

function SelectedRatesProviderOutlet() {
  return (
    <SelectedRatesProvider>
      <Outlet />
    </SelectedRatesProvider>
  );
}

// --- router ---
const routes = [
  {
    element: <WithAuthProvider />,
    children: [
      {
        element: <GuestOnly />,
        children: [
          { path: '/login', element: withSuspense(SignInPage) },
        ],
      },
      // Deliberately left reachable while signed in. Both are followed from a
      // link in an email, and both carry a token that identifies the account
      // the link is for — which need not be the account currently signed in on
      // this browser. Bouncing a signed-in user to /user-home would strand
      // anyone activating a second account or resetting a password from a
      // machine where someone else is logged in.
      { path: '/activate', element: withSuspense(ActivatePage) },
      { path: '/reset-password', element: withSuspense(ResetPasswordPage) },

      {
        element: <Protected />,
        children: [
          {
            path: '/',
            element: <AppShell />,
            children: [
              {
                path: 'user-home',
                element: withSuspense(HomePage),
                handle: { crumb: 'Home', title: 'Home' },
              },
              {
                path: 'profile',
                element: withSuspense(ProfilePage),
                handle: { crumb: 'Profile', title: 'Profile Overview' },
              },
              {
                element: <RequirePermission ability="book reservations" />,
                children: [
                  {
                    path: 'reservations',
                    element: withSuspense(MyReservationsPage),
                    // hideCrumbs: page has its own sticky search+tabs header,
                    // and the default breadcrumbs row was pushing that header
                    // ~100px below the viewport top ("floating in the middle").
                    // See shared/components/layout/PageBreadcrumbs.jsx.
                    handle: { crumb: 'Reservations', title: 'My Reservations', hideCrumbs: true },
                  },
                  {
                    // Change a booking's dates — same room, same rate (ADR-0035).
                    path: 'reservations/:confirmationId/change',
                    element: withSuspense(ChangeBookingPage),
                    handle: {
                      crumbs: [
                        { label: 'Reservations', to: '/reservations' },
                        { label: 'Change booking' },
                      ],
                      title: 'Change booking',
                    },
                  },
                ],
              },
       
              {
                element: <RequirePermission ability="view users" />,
                children: [
                  {
                    path: 'users',
                    element: withSuspense(UserManagement),
                    handle: { crumb: 'User Management', title: 'User Management' },
                  },
                ],
              },
              {
                element: <RequirePermission ability="manage hotel chains" />,
                children: [
                  {
                    path: 'chains',
                    element: withSuspense(ChainsPage),
                    handle: { crumb: 'Hotel Chains', title: 'Hotel Chains' },
                  },
                ],
              },
              {
                element: <RequirePermission ability="view vendors" />,
                children: [
                  {
                    path: 'vendors',
                    element: withSuspense(VendorsListPage),
                    handle: { crumb: 'Vendors', title: 'Vendors' },
                  },
                ],
              },
              {
                element: <RequirePermission ability="view customers" />,
                children: [
                  {
                    path: 'customers',
                    element: withSuspense(CustomersPage),
                    handle: { crumb: 'Customers', title: 'Customers' },
                  },
                  {
                    path: 'customers/:id',
                    element: withSuspense(CustomerDetailPage),
                    handle: {
                      crumbs: [
                        { label: 'Customers', to: '/customers' },
                        { label: 'Customer' },
                      ],
                      title: 'Customer',
                    },
                  },
                ],
              },
              // /vendors/create route removed — Add Vendor now opens
              // VendorFormDialog inline from the list page. Edit stays
              // as a full page at /vendors/:id/edit below.
              {
                element: <RequirePermission ability="edit vendors" />,
                children: [
                  {
                    path: 'vendors/derby-status',
                    element: withSuspense(DerbyStatusManagementPage),
                    handle: {
                      crumbs: [
                        { label: 'Vendors', to: '/vendors' },
                        { label: 'Derby Status' },
                      ],
                      title: 'Derby Status',
                    },
                  },
                  // /vendors/:id/edit route retired 2026-08-10. Edit
                  // now opens VendorFormDialog inline from the list
                  // page — same modal Create uses. See
                  // features/vendors/components/VendorFormDialog.jsx.
                ],
              },
              {
                element: <RequirePermission ability="view transactions" />,
                children: [
                  // The /billing root is the MSI "generate batch from
                  // candidates" dashboard — calls /api/billing/candidates
                  // which requires `manage billing`. Block TMs at the route
                  // level so they don't hit the 403 toast.
                  {
                    element: <RequireRole roles={['MSI Staff', 'Admin', 'Super Admin']} />,
                    children: [
                      {
                        path: 'billing',
                        element: withSuspense(BillingPage),
                        handle: { crumb: 'Billing', title: 'Billing Batches' },
                      },
                    ],
                  },
                  {
                    path: 'billing/batches',
                    element: withSuspense(BillingBatchesPage),
                    handle: { crumb: 'Batches', title: 'All Batches' },
                  },
                  {
                    path: 'billing/payments',
                    element: withSuspense(PaymentsPage),
                    handle: { crumb: 'Payment History', title: 'Payment History' },
                  },
                  {
                    path: 'reports',
                    element: withSuspense(ReportsShell),
                    handle: { crumb: 'Reports', title: 'Reports' },
                    children: [
                      { index: true, element: <Navigate to="/reports/lodging-analysis" replace /> },
                      {
                        path: 'lodging-analysis',
                        element: withSuspense(LodgingAnalysisReportPage),
                        handle: { crumb: 'Lodging Analysis', title: 'Lodging Analysis Report' },
                      },
                      {
                        path: 'invoice',
                        element: withSuspense(InvoiceReportPage),
                        handle: { crumb: 'Invoice Report', title: 'Invoice Report' },
                      },
                    ],
                  },
                  {
                    path: 'billing/:id',
                    element: withSuspense(BillingBatchDetailPage),
                    handle: {
                      crumbs: [
                        { label: 'Billing', to: '/billing' },
                        { label: 'Batch Detail' },
                      ],
                      title: 'Billing Batch',
                    },
                  },
                  // MSI-only billing routes — the /billing dashboard (which
                  // is actually the "generate batch from candidates"
                  // workflow), write actions, and the Billed Reservations
                  // investigation tool. Travel Managers navigating to these
                  // URLs are redirected to /user-home. Backend also blocks
                  // via the `manage billing` permission for writes; this is
                  // the client-side equivalent.
                  {
                    element: <RequireRole roles={['MSI Staff', 'Admin', 'Super Admin']} />,
                    children: [
                      {
                        path: 'billing/record-payment',
                        element: withSuspense(RecordPaymentPage),
                        handle: { crumb: 'Record Payment', title: 'Record Payment' },
                      },
                      {
                        path: 'billing/record-payment/process',
                        element: withSuspense(RecordMultipleBatchPaymentPage),
                        handle: { crumb: 'Process Payment', title: 'Process Payment' },
                      },
                      {
                        // Every hotel charge and where its money is (billing ADR-025).
                        path: 'billing/charges',
                        element: withSuspense(HotelChargesPage),
                        handle: {
                          crumbs: [
                            { label: 'Billing', to: '/billing' },
                            { label: 'Hotel Charges' },
                          ],
                          title: 'Hotel Charges',
                        },
                      },
                      {
                        path: 'billing/stays/:reservationId',
                        element: withSuspense(StayLedgerPage),
                        handle: {
                          crumbs: [
                            { label: 'Billing', to: '/billing' },
                            { label: 'Hotel Charges', to: '/billing/charges?view=stays' },
                            { label: 'Stay' },
                          ],
                          title: 'Stay',
                        },
                      },
                      // Billed Reservations became Hotel Charges' "By stay" view
                      // on 2026-09-29; old links and bookmarks land there.
                      { path: 'billing/billed-reservations', element: <Navigate to="/billing/charges?view=stays" replace /> },
                      { path: 'billing/billed-reservations/:reservationId', element: <RedirectToStay /> },
                    ],
                  },
                ],
              },
              {
                element: <RequirePermission ability="view employees" />,
                children: [
                  {
                    path: 'employees',
                    element: withSuspense(EmployeesListPage),
                    handle: { crumb: 'Employees', title: 'Employees' },
                  },
                  {
                    path: 'staff-reservations',
                    element: withSuspense(StaffReservationsPage),
                    handle: { crumb: 'Reservations', title: 'Reservations' },
                  },
                  // Manual reservation create/edit + standalone VCC issuance
                  // are MSI-only. Travel Managers can VIEW the
                  // staff-reservations list (scoped to their customer) but
                  // cannot create or edit manual entries or issue cards.
                  // Backend enforces this too (assertMsiStaff in controller),
                  // but this prevents the page from even loading.
                  {
                    element: <RequireRole roles={['MSI Staff', 'Admin', 'Super Admin']} />,
                    children: [
                      {
                        path: 'staff-reservations/create',
                        element: withSuspense(CreateManualReservationPage),
                        handle: { crumb: 'Create Reservation', title: 'Create Manual Reservation' },
                      },
                      {
                        path: 'staff-reservations/:id/edit',
                        element: withSuspense(CreateManualReservationPage),
                        handle: { crumb: 'Edit Reservation', title: 'Edit Manual Reservation' },
                      },
                      {
                        path: 'vcc-cards',
                        element: withSuspense(VccCardsListPage),
                        handle: { crumb: 'VCCs', title: 'VCCs' },
                      },
                    ],
                  },
                ],
              },
              {
                // Bulk CSV import is admin-only. TMs can add employees one
                // at a time via the Add Employee modal but cannot mass-
                // onboard. Backend enforces the same gate via the
                // `bulk import employees` permission on the import routes.
                element: <RequirePermission ability="bulk import employees" />,
                children: [
                  {
                    path: 'employees/imports',
                    element: withSuspense(EmployeeImportPage),
                    handle: {
                      crumbs: [
                        { label: 'Employees', to: '/employees' },
                        { label: 'Import Employees' },
                      ],
                      title: 'Import Employees',
                    },
                  },
                ],
              },
              {
                element: <RequirePermission ability="manage customer fees" />,
                children: [
                  {
                    path: 'customer-fees',
                    element: withSuspense(CustomerFeesPage),
                    handle: { crumb: 'Customer Fees', title: 'Customer Fees' },
                  },
                ],
              },
              {
                // Integrations UI is Admin-only via the narrower `manage
                // integrations` permission. It was previously grouped under
                // `view transactions` alongside payment-exceptions, but that
                // permission is also held by Travel Managers (for billing
                // reads) — grouping them together let TMs into the QuickBooks
                // controls, which is what F-1 in the review flagged.
                element: <RequirePermission ability="manage integrations" />,
                children: [
                  {
                    path: 'integrations',
                    element: withSuspense(IntegrationsPage),
                    handle: { crumb: 'Integrations', title: 'Integrations' },
                  },
                  {
                    // Error logs — same permission gate as /integrations
                    // (operational admin data). See
                    // docs/architecture/error-tracking-plan.md.
                    path: 'admin/errors',
                    element: withSuspense(ErrorLogsPage),
                    handle: { crumb: 'Error logs', title: 'Error logs' },
                  },
                  {
                    path: 'admin/errors/:id',
                    element: withSuspense(ErrorLogDetailPage),
                    handle: {
                      crumbs: [
                        { label: 'Error logs', to: '/admin/errors' },
                        { label: 'Detail' },
                      ],
                      title: 'Error detail',
                    },
                  },
                ],
              },
              {
                // Seeder: Super Admin only. Browser wrapper around
                // billing:seed-bulk artisan command so we don't have to
                // SSH to web17 every time. Backend enforces the same role.
                element: <RequireRole roles={['Super Admin']} />,
                children: [
                  {
                    path: 'admin/seeder',
                    element: withSuspense(SeederPage),
                    handle: { crumb: 'Seeder', title: 'Data Seeder' },
                  },
                ],
              },
              {
                element: <RequirePermission ability="view transactions" />,
                children: [
                  {
                    path: 'payment-exceptions',
                    element: withSuspense(PaymentExceptionsPage),
                    handle: { crumb: 'Payment Exceptions', title: 'Payment Exceptions' },
                  },
                ],
              },
              {
                element: <RequirePermission ability="view policies" />,
                children: [
                  {
                    path: 'policies',
                    element: withSuspense(PoliciesPage),
                    handle: { crumb: 'Policies', title: 'Policies' },
                  },
                ],
              },
              {
                element: <RequirePermission ability="view tags" />,
                children: [
                  {
                    path: 'tags',
                    element: withSuspense(TagsPage),
                    handle: { crumb: 'Tags', title: 'Tags' },
                  },
                  {
                    path: 'tags/:id',
                    element: withSuspense(TagDetailPage),
                    handle: { crumb: 'Tag', title: 'Tag detail' },
                  },
                ],
              },
              {
                element: <RequirePermission ability="view policy reviews" />,
                children: [
                  {
                    path: 'policy-reviews',
                    element: withSuspense(PolicyReviewsPage),
                    handle: { crumb: 'Policy Reviews', title: 'Policy Reviews' },
                  },
                ],
              },
              { index: true, element: <Navigate to="user-home" replace /> },
              {
                element: <SelectedRatesProviderOutlet />,
                children: [
                  {
                    path: 'booking',
                    element: withSuspense(HotelSearchPage),
                    handle: { crumb: 'Reservation', title: 'Reservation Search' },
                  },
                  {
                    path: 'booking/:hotelId',
                    element: withSuspense(HotelDetailPage),
                    handle: {
                      crumbs: [
                        { label: 'Reservation', to: '/booking' },
                        { label: 'Hotel Details' },
                      ],
                      title: 'Hotel Details',
                    },
                  },
                  {
                    path: 'booking/:hotelId/modify/:confirmationId',
                    element: <RedirectToChangeBooking />,
                  },
                  {
                    path: 'booking/confirmation/:confirmationId',
                    element: withSuspense(BookingFinalConfirmationPage),
                    handle: {
                      crumbs: [
                        { label: 'Reservation', to: '/booking' },
                        { label: 'Confirmation' },
                      ],
                      title: 'Confirmation',
                    },
                  },
                  {
                    path: 'booking/group-confirmation/:groupId',
                    element: withSuspense(GroupConfirmationPage),
                    handle: {
                      crumbs: [
                        { label: 'Reservation', to: '/booking' },
                        { label: 'Group Confirmation' },
                      ],
                      title: 'Group Confirmation',
                    },
                  },
                  // ── URL-embedded flow ID routes ────────────────────────
                  // Every mid-flow step (review, guests, payment) embeds
                  // its cartId (/c/) or groupId (/g/) in the URL so a
                  // browser refresh recovers via useBookingRestore.
                  //
                  // Prefix legend: /c/ = cart-based (single-rate),
                  //                /g/ = group (multi-rate).
                  // Matches the Stripe checkout URL convention where the
                  // resource type is readable from the URL.
                  {
                    path: 'booking/:hotelId/c/:cartId/review',
                    element: withSuspense(BookingConfirmationPage),
                    handle: {
                      crumbs: [
                        { label: 'Reservation', to: '/booking' },
                        { label: 'Confirm Reservation' },
                      ],
                      title: 'Confirm Reservation',
                    },
                  },
                  {
                    path: 'booking/:hotelId/c/:cartId/guests',
                    element: withSuspense(BookingGuestDetailsPage),
                    handle: {
                      crumbs: [
                        { label: 'Reservation', to: '/booking' },
                        { label: 'Guest Details' },
                      ],
                      title: 'Guest Details',
                    },
                  },
                  {
                    path: 'booking/:hotelId/c/:cartId/payment',
                    element: withSuspense(BookingPaymentPage),
                    handle: {
                      crumbs: [
                        { label: 'Reservation', to: '/booking' },
                        { label: 'Payment' },
                      ],
                      title: 'Payment',
                    },
                  },
                  {
                    path: 'booking/:hotelId/g/:groupId/review',
                    element: withSuspense(BookingConfirmationPage),
                    handle: {
                      crumbs: [
                        { label: 'Reservation', to: '/booking' },
                        { label: 'Confirm Reservation' },
                      ],
                      title: 'Confirm Reservation',
                    },
                  },
                  {
                    path: 'booking/:hotelId/g/:groupId/guests',
                    element: withSuspense(BookingGuestDetailsPage),
                    handle: {
                      crumbs: [
                        { label: 'Reservation', to: '/booking' },
                        { label: 'Guest Details' },
                      ],
                      title: 'Guest Details',
                    },
                  },
                  {
                    path: 'booking/:hotelId/g/:groupId/payment',
                    element: withSuspense(BookingPaymentPage),
                    handle: {
                      crumbs: [
                        { label: 'Reservation', to: '/booking' },
                        { label: 'Payment' },
                      ],
                      title: 'Payment',
                    },
                  },
                  {
                    path: 'booking/:hotelId/g/:groupId/confirmation',
                    element: withSuspense(GroupConfirmationPage),
                    handle: {
                      crumbs: [
                        { label: 'Reservation', to: '/booking' },
                        { label: 'Group Confirmation' },
                      ],
                      title: 'Group Confirmation',
                    },
                  },
                ],
              },
            ],
          },
        ],
      },
    ],
  },
];

const router = createBrowserRouter(routes);

export default router;
export { routes };
